import React, {useState, useEffect} from "react";
import {Form, Col, Row, Container} from "react-bootstrap";
import PropTypes from 'prop-types';
import BackNext from '../../Common/SubComponents/BackNext';
import {useForm} from 'react-hook-form/dist/react-hook-form.ie11';
import {customValidators, object, string, ref} from '../../../Helpers/Validators/customValidators'
import {SelectInput, TextInput} from "../../../Helpers/Inputs";
import {GoogleCaptcha} from "../../Common/SubComponents/GoogleCaptcha";
import EmojiObjectsIcon from '@material-ui/icons/EmojiObjects';
import {InputMaskPhone} from '../../../Helpers/Formatters';
import Alert from '@material-ui/lab/Alert';
import Step1Icon from '../icons/step_1.svg';
import SvgIcon from '@material-ui/core/SvgIcon';
import {SESSION_STORAGE_ACCOUNT_KEY, getSessionStorageItem} from '../../../../sessionStorage';

export default function PersonalInfo(props) {

  const accountObj = getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY);
  const skipCaptcha = process.env.REACT_APP_DISABLE_CAPTCHA === 'true';

  const {register, errors, handleSubmit, setValue, triggerValidation} = useForm({
    validationSchema: PersonalInfo.ValidationSchema,
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  const [validCaptcha, setValidCaptcha] = useState(skipCaptcha ? true : null);

  const handleCaptchaChange = (responseToken) => {
    if (responseToken) {
      setValidCaptcha(true);
    }
  }

  const submitForm = (e) => {
    if (validCaptcha) {
      props.onNext();
    } else {
      setValidCaptcha(false);
    }
  }

  const handleValueChange = (field, value) => {
    setValue(field, value);
    props.onFieldChange(field, value);
    triggerValidation(field);
  };

  useEffect(() => {
    if (props.basicInfo.email) {
      handleValueChange('emailAddress', props.basicInfo.email);
    }
  }, []);

  return (
    <Container>
      <Row className="formPage justify-content-center">
        <Col lg={6}>
          <Row className="justify-content-center">
            <SvgIcon component={Step1Icon} className="headerIcon"/>
          </Row>
          <h4 className="headerTitle">Let's Begin. Safety First.</h4>
          <p className="creating-pin">Creating a PIN will secure your personal information.</p>

          {!skipCaptcha ? (
            <Row className="justify-content-center">
              <GoogleCaptcha handleCaptchaChange={handleCaptchaChange} showError={validCaptcha === false}/>
            </Row>
          ) : (
            <Row className="justify-content-center border mt-2 mb-2">
              <span className="text-black-50 mt-2 mb-2">CAPTCHA Disabled By Config</span>
            </Row>
          )}

          <form className="quote-form" onSubmit={handleSubmit(submitForm)}>

            <p className="enter-pin">
              Enter and confirm a PIN to be able to return to your session. All information is secure and confidential.
            </p>

            <Row>
              <Col>
                <Form.Group>
                  <TextInput
                    type="password"
                    name="pin"
                    id="pin"
                    label="4 Digit PIN"
                    value={props.formState.pin}
                    onChange={e => props.onFieldChange('pin', e.target.value)}
                    errors={errors.pin}
                    inputRef={register}
                    inputProps={{
                      maxLength: 4,
                    }}
                  />
                </Form.Group>
              </Col>
              <Col>
                <TextInput
                  type="password"
                  name="pinConfirm"
                  id="pinConfirm"
                  label="Confirm your PIN"
                  value={props.formState.pinConfirm}
                  onChange={e => props.onFieldChange('pinConfirm', e.target.value)}
                  errors={errors.pinConfirm}
                  inputRef={register}
                  inputProps={{
                    maxLength: 4,
                  }}
                />
              </Col>
            </Row>

            <Form.Group>
              <TextInput
                name="firstName"
                id="firstName"
                label="First Name"
                value={props.formState.firstName}
                onChange={e => props.onFieldChange('firstName', e.target.value)}
                errors={errors.firstName}
                inputRef={register}
              />
            </Form.Group>

            <Form.Group>
              <TextInput
                name="middleInitial"
                id="middleInitial"
                label="Middle Initial"
                value={props.formState.middleInitial}
                onChange={e => props.onFieldChange('middleInitial', e.target.value)}
                errors={errors.middleInitial}
                inputRef={register}
              />
            </Form.Group>

            <Form.Group>
              <TextInput
                name="lastName"
                id="lastName"
                label="Last Name"
                value={props.formState.lastName}
                onChange={e => props.onFieldChange('lastName', e.target.value)}
                errors={errors.lastName}
                inputRef={register}
              />
            </Form.Group>

            <Form.Group>
              <SelectInput
                name="insuredPhoneType"
                id="insuredPhoneType"
                label="Phone Number Type"
                value={props.formState.insuredPhoneType}
                selectOptions={[{value: "home", label: "Home"}, {value: "cell", label: "Cell"}, {
                  value: "work",
                  label: "Work"
                }]}
                onChange={e => handleValueChange('insuredPhoneType', e.target.value)}
                errors={errors.insuredPhoneType}
                inputRef={register}
              />
            </Form.Group>

            <Form.Group>
              <TextInput
                name="insuredPhoneNumber"
                id="insuredPhoneNumber"
                label="Phone Number"
                value={props.formState.insuredPhoneNumber}
                onChange={e => props.onFieldChange('insuredPhoneNumber', e.target.value)}
                errors={errors.insuredPhoneNumber}
                inputRef={register}
                InputProps={{
                  inputComponent: InputMaskPhone,
                }}
              />
            </Form.Group>

            <Form.Group>
              <TextInput
                name="emailAddress"
                id="emailAddress"
                label="Email Address"
                value={props.formState.emailAddress}
                onChange={e => props.onFieldChange('emailAddress', e.target.value)}
                errors={errors.emailAddress}
                inputRef={register}
              />
            </Form.Group>

            <Alert className="mt-3 mb-3" severity="success" icon={<EmojiObjectsIcon/>}>
              Insider Tip: This site is completely secure through 256bit TLS encryption. All necessary precautions and
              technologies have been put in place to protect our customers and their personal information.
            </Alert>

            <BackNext onBack={props.onBack}/>


          </form>
        </Col>

        <p className="disclaimer">By clicking Continue, you are providing your signed permission
          for {accountObj.account.isSAAS ? accountObj.account.accountName : 'Covr Financial Technologies'} to contact
          you at the email and telephone number provided, including via text message and live, automated and
          pre-recorded telephone calls, even if your number is on a Do Not Call list. You also acknowledge that you have
          received and agree to our Privacy Policy, Terms of Use, and Consent to Do Business Electronically. You may
          revoke these consents at any time by emailing us at <a
            href="mailto:support@covrtech.com">support@covrtech.com</a>.</p>


      </Row>

    </Container>
  )
}

PersonalInfo.ValidationSchema = object().shape({
  pin: string()
    .required('PIN is required.')
    .length(4)
    .matches(/\d{4}/, 'PIN must be 4 numeric digits.'),
  pinConfirm: string()
    .required('PIN confirm is required.')
    .oneOf([ref('pin')], 'PINs must match.'),
  firstName: customValidators.string()
    .isValidNameLimitedSpecialChar('First name is required.', true, false),
  middleInitial: string()
    .matches(/^$|^(?!\s*$)[ a-zA-ZÀ-ÖØ-öø-ÿ'.-]{1}$/, 'Must be single alpha character.')
    .notRequired(),
  lastName: customValidators.string()
    .isValidNameLimitedSpecialChar('Last name is required.', true, false),
  insuredPhoneType: string().required('Phone Number Type is required.'),
  insuredPhoneNumber: customValidators.string()
    .required('Phone Number is required.')
    .isValidPhone('Phone Number must be formatted as "(123) 456-7890".'),
  emailAddress: string()
    .email('Invalid email')
    .required('Email is required.'),
});

PersonalInfo.DefaultFormState = (prefill = false, basicInfo = null) => {
  return {
    pin: prefill ? "1111" : "",
    pinConfirm: prefill ? "1111" : "",
    firstName: prefill ? "Proposed" : (basicInfo && basicInfo.firstName) || '',
    middleInitial: prefill ? "N" : '',
    lastName: prefill ? "Sured" : (basicInfo && basicInfo.lastName) || '',
    insuredPhoneType: prefill ? "home" : "",
    insuredPhoneNumber: prefill ? "(769) 345-4567" : (basicInfo && basicInfo.phone) || '',
    emailAddress: prefill ? 'user@address.com' : (basicInfo && basicInfo.email) || '',
  }
}

PersonalInfo.propTypes = {
  formState: PropTypes.object.isRequired,
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onFieldChange: PropTypes.func.isRequired,
}



// WEBPACK FOOTER //
// ./src/components/Quoters/Americo/steps/PersonalInfo.js